import { bgColorsVars } from '@design-system/vars.css';
import { CircularProgress, MenuItem, Select, Tooltip } from '@mui/material';
import { Control, Controller, Path } from 'react-hook-form';

type SelectOption = {
  label: string;
  value: string | number | null;
  disabled?: boolean;
};

export const RHFSelect = <T extends object>({
  control,
  name,
  options,
  hasError,
  tooltipText,
  required,
  disabled,
  loading,
}: {
  control: Control<T, any, T>;
  name: Path<T>;
  options: SelectOption[];
  hasError?: boolean;
  tooltipText?: Record<number, string>;
  required?: boolean;
  disabled?: boolean;
  loading?: boolean;
}) => (
  <Controller
    name={name}
    control={control}
    render={({ field }) => (
      <Select
        id={`select-${name}`}
        {...field}
        error={hasError}
        required={!!required}
        displayEmpty={true}
        renderValue={(value: string | number | null) => {
          if (loading) return 'Loading...';
          if (field.value)
            return options.find((i) => i.value === field.value)?.label;
          return 'Select';
        }}
        className="shadow-none border-2 rounded-[10px] px-4 py-2"
        disabled={disabled}
      >
        {loading ? (
          <MenuItem disabled>
            <CircularProgress size={16} sx={{ marginRight: 1 }} />
            Loading...
          </MenuItem>
        ) : (
          options.map((option) => (
            <MenuItem
              key={option.label}
              value={option.value ?? undefined}
              id={`select-${option.value}`}
              disabled={!!option.disabled}
            >
              <Tooltip
                title={
                  typeof option.value === 'number' &&
                  tooltipText?.[option.value]
                    ? tooltipText[option.value]
                    : ''
                }
                placement="right"
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: bgColorsVars['N-40'],
                      color: 'black',
                      fontSize: '14px',
                      fontWeight: 500,
                      fontFamily: 'inherit',
                      padding: '4px 8px',
                    },
                  },
                }}
              >
                <div>{option.label}</div>
              </Tooltip>
            </MenuItem>
          ))
        )}
      </Select>
    )}
  />
);
